import { queueToast } from '../utils/toast';

// In dev the CRA proxy forwards these paths to REACT_APP_API_URL, so same-origin
// requests are used — this avoids third-party cookie blocking in the browser.
const API = process.env.NODE_ENV === 'development' ? '' : process.env.REACT_APP_API_URL;

// ── In-memory access token (never stored in localStorage) ─────────────────
let _accessToken = null;
let _refreshPromise = null;

// ── User profile — non-sensitive, safe to persist across reloads ───────────
const PROFILE_KEY = 'reano_profile';
function saveProfile(p) { try { localStorage.setItem(PROFILE_KEY, JSON.stringify(p)); } catch {} }
function clearProfile()  { try { localStorage.removeItem(PROFILE_KEY); } catch {} }
export function getProfile() { try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || 'null'); } catch { return null; } }

// ── JWT helpers ────────────────────────────────────────────────────────────
function decodeJwtPayload(token) {
  try { return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); }
  catch { return null; }
}
function tokenExpiresInSeconds(token) {
  const p = decodeJwtPayload(token);
  return p?.exp ? p.exp - Math.floor(Date.now() / 1000) : 0;
}

// ── Core refresh (called by getValidToken and the 401 interceptor) ─────────
async function doRefresh() {
  const res = await fetch(`${API}/auth/refresh`, { method: 'POST', credentials: 'include' });
  if (res.status === 401) {
    _accessToken = null;
    clearProfile();
    queueToast('Your session has expired. Please sign in again.');
    window.location.href = '/auth';
    throw new Error('Session expired.');
  }
  if (!res.ok) throw new Error('Failed to refresh session.');
  const data = await res.json();
  _accessToken = data.accessToken;
  return data.accessToken;
}

// ── Single-flight token getter ─────────────────────────────────────────────
export async function getValidToken() {
  if (_accessToken && tokenExpiresInSeconds(_accessToken) > 60) return _accessToken;
  if (!_refreshPromise) {
    _refreshPromise = doRefresh().finally(() => { _refreshPromise = null; });
  }
  return _refreshPromise;
}

// ── Authenticated fetch with automatic 401 retry ───────────────────────────
export async function fetchAuth(url, options = {}) {
  const makeReq = (token) => fetch(url, {
    ...options,
    credentials: 'include',
    headers: { ...options.headers, Authorization: `Bearer ${token}` },
  });

  let token = await getValidToken();
  let res   = await makeReq(token);
  if (res.status !== 401) return res;

  // Force a refresh (share any in-flight one) then retry once
  if (!_refreshPromise) {
    _refreshPromise = doRefresh().finally(() => { _refreshPromise = null; });
  }
  const newToken = await _refreshPromise;
  return makeReq(newToken);
}

// ── Restore session on page load ───────────────────────────────────────────
export async function restoreSession() {
  try {
    const res = await fetch(`${API}/auth/refresh`, { method: 'POST', credentials: 'include' });
    if (!res.ok) { clearProfile(); return null; }
    const data = await res.json();
    _accessToken = data.accessToken;
    return getProfile();
  } catch { return null; }
}

// ── Auth API calls ─────────────────────────────────────────────────────────
export async function verifyCompany(companyId) {
  const res = await fetch(`${API}/auth/company/${encodeURIComponent(companyId.trim())}`, {
    credentials: 'include',
  });
  if (res.status === 404) return { found: false, name: '' };
  if (!res.ok) throw new Error('Failed to verify company.');
  const data = await res.json();
  return { found: true, name: data.companyName };
}

export async function createCompany(companyName) {
  const companyId = companyName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const res = await fetch(`${API}/auth/create-company`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyName: companyName.trim(), companyId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || 'Failed to create company.');
  return { companyId: data.companyId, name: data.companyName };
}

export async function checkEmail(email, companyId) {
  const res = await fetch(`${API}/auth/check-user`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, email: email.toLowerCase().trim() }),
  });
  const data = await res.json();
  if (res.status === 404) {
    if (data.error === 'Company not found') throw new Error('Company not found. Please go back and check your company ID.');
    return { exists: false };
  }
  if (!res.ok) throw new Error(data.error || 'Failed to check email.');
  return { exists: true, name: data.name };
}

function buildProfile(user, companyName) {
  return {
    id:          user.userId,
    email:       user.email,
    name:        user.name,
    companyId:   user.companyId,
    companyName: companyName,
    isActive:    user.isActive === true,
  };
}

export async function login(companyId, email, password) {
  const res = await fetch(`${API}/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, email: email.toLowerCase().trim(), password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Invalid credentials.');
  _accessToken = data.accessToken;
  const profile = buildProfile(data.user, data.company.companyName);
  saveProfile(profile);
  return profile;
}

export async function signup({ firstName, lastName, email, password, phone, companyId, companyName }) {
  const res = await fetch(`${API}/auth/create-user`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      companyId,
      email: email.toLowerCase().trim(),
      name: `${firstName.trim()} ${lastName.trim()}`,
      phone,
      password,
      role: 'admin',
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create account.');
  _accessToken = data.accessToken;
  const profile = buildProfile(data.user, companyName);
  saveProfile(profile);
  return profile;
}

export async function logout() {
  try {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
  } catch {}
  _accessToken = null;
  clearProfile();
}

export async function forgotPassword(companyId, email) {
  const res = await fetch(`${API}/auth/forgot-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, email: email.toLowerCase().trim() }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to send reset code.');
}

export async function resetPassword(companyId, email, code, newPassword) {
  const res = await fetch(`${API}/auth/reset-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, email: email.toLowerCase().trim(), code, newPassword }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to reset password.');
}

export async function verifyEmail(companyId, email, code) {
  const res = await fetch(`${API}/auth/verify-email`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, email: email.toLowerCase().trim(), code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to verify email.');
}

const VALID_PLANS = new Set(['free', 'starter', 'growth', 'pro']);

function normalizePlan(plan) {
  const lower = (plan ?? '').toLowerCase();
  return VALID_PLANS.has(lower) ? lower : 'free';
}

function normalizeItems(items) {
  return (items ?? []).map(item => ({
    ...item,
    productId: item.productId ?? 'pulse',
    plan: normalizePlan(item.plan),
  }));
}

export async function getAccess() {
  try {
    const res = await fetchAuth(`${API}/access`);
    if (!res.ok) return { emailVerified: true, items: [] };
    const data = await res.json();
    if (Array.isArray(data)) return { emailVerified: true, items: normalizeItems(data) };
    return { emailVerified: data.emailVerified ?? true, items: normalizeItems(data.items) };
  } catch { return { emailVerified: true, items: [] }; }
}

export async function verifyEmailToken(token) {
  const res = await fetch(`${API}/auth/verify-email?token=${encodeURIComponent(token)}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Invalid or expired link.');
  }
}
